import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Play, Square } from "lucide-react";

/**
 * Driver Clock In / Clock Out control.
 *
 * Rendered at the top of the Driver PWA regardless of whether a route is
 * loaded — driver_helpers who never see a route still need to punch in.
 *
 * Polls `/timeclock/status` every 60 s so the running-hours counter stays
 * fresh across long shifts. The counter itself ticks locally every second
 * without pinging the server (cheap, smooth).
 */
const CT = "America/Chicago";

function fmtCTTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString("en-US", {
        timeZone: CT, hour: "numeric", minute: "2-digit", hour12: true,
    });
}

function fmtDuration(ms) {
    if (!ms || ms < 0) return "0:00";
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}:${m.toString().padStart(2, "0")}`;
}

export default function DriverClockCard() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tick, setTick] = useState(0); // forces re-render for the timer
    const mountedRef = useRef(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/timeclock/status");
            if (mountedRef.current) setStatus(data);
        } catch {
            // Non-fatal — driver may have lost signal. Keep last-known status.
        }
    }, []);

    // Poll every 60 s and re-render the running counter every second.
    useEffect(() => {
        mountedRef.current = true;
        load();
        const poll = setInterval(load, 60_000);
        const timer = setInterval(() => setTick((t) => t + 1), 1000);
        return () => {
            mountedRef.current = false;
            clearInterval(poll);
            clearInterval(timer);
        };
    }, [load]);

    const canPunch = status && (status.role === "driver" || status.role === "driver_helper");
    if (!canPunch) return null; // admins/dispatchers see nothing

    const clockIn = async () => {
        setBusy(true);
        try {
            await api.post("/timeclock/clock-in", {});
            toast.success("Clocked in — have a good shift");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clock-in failed");
        } finally { setBusy(false); }
    };

    const clockOut = async () => {
        if (!window.confirm("Clock out now?")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/timeclock/clock-out", {});
            const hrs = data?.entry?.hours ?? 0;
            toast.success(`Clocked out · ${hrs} hrs this session`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clock-out failed");
        } finally { setBusy(false); }
    };

    const clockedIn = !!status?.is_clocked_in;
    const running = clockedIn && status?.entry?.clock_in
        ? Date.now() - new Date(status.entry.clock_in).getTime()
        : 0;

    // Reference `tick` so React re-renders the duration each second.
    void tick;

    return (
        <div className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${
            clockedIn
                ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500/30"
                : "bg-secondary/50 border-border"
        }`} data-testid="driver-clock-card">
            <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                    clockedIn ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"
                }`}>
                    <Clock className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {clockedIn ? "On the clock" : "Not clocked in"}
                    </div>
                    {clockedIn ? (
                        <div className="text-sm font-semibold" data-testid="driver-clock-since">
                            Since {fmtCTTime(status.entry.clock_in)} · <span className="font-mono">{fmtDuration(running)}</span>
                        </div>
                    ) : (
                        <div className="text-xs text-muted-foreground">
                            Tap the button when your shift begins.
                        </div>
                    )}
                </div>
            </div>
            {clockedIn ? (
                <Button size="sm" variant="destructive" onClick={clockOut} disabled={busy}
                        data-testid="driver-clock-out-btn">
                    <Square className="w-3.5 h-3.5 mr-1.5" />Clock out
                </Button>
            ) : (
                <Button size="sm" onClick={clockIn} disabled={busy}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="driver-clock-in-btn">
                    <Play className="w-3.5 h-3.5 mr-1.5" />Clock in
                </Button>
            )}
        </div>
    );
}
